import { JSX, MouseEvent, ReactNode } from 'react';

import { Badge } from '../common';
import { EllipsisText } from '../../EllipsisText';
import { Box, BoxColors } from '../../Box';
import { Typography } from '../../Typography';
import { Tooltip, type TooltipProps } from '../../Tooltip';
import { Checkbox } from '../../Checkbox';

import { Option } from './dropdownOption.types';

/**
 * Расширенный интерфейс опции с дополнительными полями.
 */
interface ExtendedOption extends Option {
  /**
   * Подзаголовок опции.
   */
  subtitle?: string;

  /**
   * Иконка в начале опции.
   */
  startIcon?: JSX.Element;
}

const COLORS = {
  gray: 'stroke.primary',
  darkgray: 'stroke.secondary',
  green: 'accent.stroke.success',
  orange: 'accent.stroke.warning',
  purple: 'accent.stroke.review',
  blue: 'accent.stroke.blue',
  red: 'accent.stroke.error',
};

/**
 * Функция renderTwoLineOption рендерит опцию с двумя строками.
 * Опция может содержать подзаголовок и значок (badge).
 *
 * @public
 * @param option - Объект опции, содержащий label и badge.
 * @returns  Компонент опции с двумя строками.
 *
 * @example
 * ```
 * <Box>
 *   {renderTwoLineOption({ label: 'Заголовок, Подзаголовок', badge: { title: 'Info', description: 'Описание', color: 'primary', position: 'top' } })}
 * </Box>
 * ```
 */
export const renderTwoLineOption = (option: Option) => {
  const [subtitle, titleFirst, titleSecond] = option.label.split(', ');
  const { badge } = option;

  const isBottom = badge?.position === 'bottom';

  let title = '';

  if (subtitle && !titleFirst) {
    title = subtitle;
  } else {
    const titleSecondFormatted = titleSecond ? `, ${titleSecond}` : '';
    title = `${titleFirst || ''}${titleSecondFormatted}`;
  }

  return (
    <Box py={badge ? '8' : '12'}>
      {title && (
        <Box>
          <Typography variant="body3">{title}</Typography>
        </Box>
      )}
      <Box
        display="flex"
        alignItems="center"
        flexDirection="row"
        gap="8"
        mt="4"
      >
        {badge && (
          <Box>
            <Badge
              title={badge.title}
              description={badge.description}
              color={badge.color}
            />
          </Box>
        )}
        {title !== subtitle && (
          <Box order={isBottom ? 0 : 1}>
            <Typography
              component="div"
              variant={title ? 'caption' : 'body3'}
              color="text.light.tertirary"
            >
              {subtitle}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * Функция renderOptionWithBadge рендерит опцию с значком (badge).
 *
 * @public
 * @param option - Объект опции, содержащий label и badge.
 * @returns  Компонент опции с значком.
 *
 * @example
 * ```
 * <Box>
 *   {renderOptionWithBadge({ label: 'Заголовок', badge: { title: 'Info', description: 'Описание', color: 'primary' } })}
 * </Box>
 * ```
 */
export const renderOptionWithBadge = (option: Option) => {
  const { badge, label } = option;

  return (
    <Box py={badge ? '8' : '12'}>
      <Box
        display="flex"
        alignItems="center"
        flexDirection="row"
        gap="8"
        mt="4"
      >
        {badge && (
          <Box>
            <Badge
              title={badge.title}
              description={badge.description}
              color={badge.color}
            />
          </Box>
        )}
        <Box>
          <Typography
            component="div"
            variant="body3"
            color="text.light.primary"
          >
            {label}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Функция renderTwoLineOptionEllipsis рендерит опцию с двумя строками,
 * где первая строка содержит основной заголовок, а вторая строка содержит
 * подзаголовок, обрезанный с помощью EllipsisText.
 *
 * @public
 * @param option - Объект опции, содержащий label.
 * @returns  Компонент опции с двумя строками и обрезанным подзаголовком.
 *
 * @example
 * ```
 * <Box>
 *   {renderTwoLineOptionEllipsis({ label: 'Заголовок, Подзаголовок, Дополнительный текст' })}
 * </Box>
 * ```
 */
export const renderTwoLineOptionEllipsis = (option: Option) => {
  const [title, ...rest] = option.label.split(',');

  return (
    <Box py="12">
      <Tooltip
        disabled={!option?.tooltipText}
        title={option.tooltipText || ''}
        placement="right"
      >
        <Box>
          <Typography variant="body3">{title}</Typography>

          <Box mt="2">
            <Typography variant="caption" color="text.light.tertirary">
              <EllipsisText>{rest?.join(', ')}</EllipsisText>
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    </Box>
  );
};

/**
 * Функция renderTwoLineWithJoinedTitleAndSubtitle рендерит опцию с двумя строками,
 * где первая строка содержит основной заголовок + подзаголовок, объединенные через запятую.
 *
 * @public
 * @param option - Объект опции, содержащий label.
 * @returns  Компонент опции с двумя строками и обрезанным подзаголовком.
 *
 * @example
 * ```
 * <Box>
 *   {renderTwoLineWithJoinedTitleAndSubtitle({ label: 'Заголовок, Подзаголовок, Дополнительный текст' })}
 * </Box>
 * ```
 */
export const renderTwoLineWithJoinedTitleAndSubtitle = (option: Option) => {
  const [title, subtitle, ...rest] = option.label.split(',');

  return (
    <Box py="12">
      <Box>
        <Typography variant="body3">{[title, subtitle].join(', ')}</Typography>
      </Box>
      <Box mt="2">
        <Typography variant="caption" color="text.light.tertirary">
          <EllipsisText>{rest?.join(', ')}</EllipsisText>
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * Функция renderLineOptionWithTooltip рендерит опцию с подсказкой (tooltip).
 * Основной текст опции обрезается с помощью EllipsisText.
 *
 * @public
 * @param option - Объект опции, содержащий label.
 * @param tooltipProps - Дополнительные свойства для Tooltip (исключая children).
 * @returns  Компонент опции с подсказкой.
 *
 * @example
 * ```
 * <Box>
 *   {renderLineOptionWithTooltip({ label: 'Длинный заголовок, который нужно обрезать' })}
 * </Box>
 * ```
 */
export const renderLineOptionWithTooltip = (
  option: Option,
  tooltipProps?: Omit<TooltipProps, 'children'>,
) => {
  const { label } = option;

  return (
    <Tooltip placement="right" title={label} {...tooltipProps}>
      <Box py="12">
        <Box py="2">
          <Typography variant="body3">
            <EllipsisText rows={1}>{label}</EllipsisText>
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

/**
 * Функция renderLineOptionWithDisabledItemAndTooltip рендерит опцию с подсказкой (tooltip),
 * если опция отключена. Иначе рендерит обычную опцию.
 *
 * @public
 * @param option - Объект опции, содержащий label, disabled и tooltipText.
 * @returns  Компонент опции с подсказкой, если она отключена, иначе обычная опция.
 *
 * @example
 * ```
 * <Box>
 *   {renderLineOptionWithDisabledItemAndTooltip({ label: 'Отключенная опция', disabled: true, tooltipText: 'Эта опция недоступна' })}
 * </Box>
 * ```
 */
export const renderLineOptionWithDisabledItemAndTooltip = (option: Option) => {
  const { disabled, label, tooltipText } = option;

  if (disabled && tooltipText) {
    return (
      <Box>
        <Tooltip title={tooltipText} placement="right">
          <Box py="12" px="16" display="inline-block" cursor="pointer">
            <Typography variant="body3" color="text.light.tertirary">
              <EllipsisText rows={1}>{label}</EllipsisText>
            </Typography>
          </Box>
        </Tooltip>
      </Box>
    );
  } else {
    return (
      <Box py="8">
        <Typography variant="body3">{label}</Typography>
      </Box>
    );
  }
};

/**
 * Функция renderTwoLineOptionWithSeparateSubtitle рендерит опцию с двумя строками,
 * где первая строка содержит основной заголовок (label), а вторая строка содержит
 * отдельный подзаголовок (subtitle).
 *
 * @public
 * @param option - Объект опции, содержащий label и subtitle.
 * @returns  Компонент опции с двумя строками и отдельным подзаголовком.
 *
 * @example
 * ```
 * <Box>
 *   {renderTwoLineOptionWithSeparateSubtitle({ label: 'Заголовок', subtitle: 'Подзаголовок' })}
 * </Box>
 * ```
 */
export const renderTwoLineOptionWithSeparateSubtitle = (
  option: ExtendedOption,
) => {
  const { subtitle, label } = option;

  return (
    <Box py="12">
      <Box>
        <Typography variant="body3">{label}</Typography>
      </Box>
      <Box
        display="flex"
        alignItems="center"
        flexDirection="row"
        gap="8"
        mt="4"
      >
        <Box>
          <Typography
            component="div"
            variant="caption"
            color="text.light.tertirary"
          >
            {subtitle}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Функция renderTwoLineOptionWithJSXLabelAndSubtitle рендерит опцию с двумя строками,
 * где первая строка может быть JSX-элементом или строкой (label), а вторая строка —
 * отдельный подзаголовок (subtitle).
 *
 * @public
 * @param label - основной текст.
 * @param subtitle - подзаголовок.
 * @returns  Компонент опции с JSX-заголовком и отдельным подзаголовком.
 *
 * @example
 * ```
 * <Box>
 *   {renderTwoLineOptionWithJSXLabelAndSubtitle({
 *     label: <Typography variant="body3" color="text.light.primary">Кастомный заголовок</Typography>,
 *     subtitle: 'Подзаголовок'
 *   })}
 * </Box>
 * ```
 */
export const renderTwoLineOptionWithNodeLabelAndSubtitle = ({
  label,
  subtitle,
}: {
  label: ReactNode | string;
  subtitle?: ReactNode | string;
}) => (
  <Box display="flex" flexDirection="column">
    <Box>{label}</Box>
    <Typography variant="caption" color="text.light.tertirary">
      {subtitle}
    </Typography>
  </Box>
);

/**
 * Функция renderTwoLineOptionWithDisabledTooltip рендерит опцию с двумя строками
 * (заголовок и подзаголовок). Если опция отключена, показывает тултип с пояснением
 * и блокирует выбор через stopPropagation.
 *
 * @public
 * @param option - Объект опции, содержащий label, subtitle, disabled и tooltipText.
 * @returns  Компонент опции с двумя строками.
 *
 * @example
 * ```
 * <Box>
 *   {renderTwoLineOptionWithDisabledTooltip({
 *     label: 'Название',
 *     subtitle: 'Тип · Грейд',
 *     disabled: true,
 *     tooltipText: 'Эта опция уже выбрана'
 *   })}
 * </Box>
 * ```
 */
export const renderTwoLineOptionWithDisabledTooltip = (
  option: ExtendedOption,
) => {
  const { label, subtitle, disabled, tooltipText } = option;

  const content = (
    <Box
      py="12"
      display="flex"
      flexDirection="column"
      gap="4"
      cursor={!disabled ? 'pointer' : 'default'}
    >
      <Typography
        variant="body3"
        color={!disabled ? 'text.light.primary' : 'text.light.tertirary'}
      >
        <EllipsisText rows={1} wordBreak="break-all">
          {label}
        </EllipsisText>
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.light.tertirary">
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  if (disabled) {
    return (
      <Box flexGrow={1} onClick={(e) => e.stopPropagation()}>
        <Tooltip title={tooltipText as string}>{content}</Tooltip>
      </Box>
    );
  }

  return content;
};

/**
 * Функция renderOptionWithColoredIndicator рендерит опцию с цветовым индикатором.
 *
 * @public
 * @param option - Объект опции, содержащий color.
 * @returns  Компонент опции с цветовым индикатором.
 *
 * @example
 * ```
 * <Box>
 *   {renderOptionWithColoredIndicator({ label: 'Заголовок', color: 'green' })}
 * </Box>
 * ```
 */
export const renderOptionWithColoredIndicator = (option: ExtendedOption) => {
  const { label, color } = option;

  return (
    <Box display="flex" alignItems="center" gap="12" py="12">
      {color && (
        <Box
          width={8}
          height={8}
          bgcolor={COLORS[color] as BoxColors}
          radius="round"
        />
      )}
      <Box>
        <Typography variant="body3">{label}</Typography>
      </Box>
    </Box>
  );
};

/**
 * Функция renderCustomOptionWithHighlight рендерит опцию с подсветкой выбранного элемента
 *
 * @public
 * @param option - Объект опции, содержащий value и label
 * @param currentValue - Текущее выбранное значение (для сравнения)
 * @returns  Компонент опции с подсветкой выбранного элемента
 *
 * @example
 * ```
 * <Box>
 *   {renderCustomOptionWithHighlight({ value: '1', label: 'Option 1' }, '1')}
 * </Box>
 * ```
 */
export const renderCustomOptionWithHighlight = (
  option: Option,
  currentValue?: string,
) => {
  return (
    <Box display="flex" alignItems="start" gap="12" py="8">
      <Typography
        variant="body3"
        color={
          option.value === currentValue
            ? 'accent.text.blue'
            : 'text.light.primary'
        }
      >
        {option.label}
      </Typography>
    </Box>
  );
};

/**
 * Функция renderTwoLineOptionWithSubtitleAndCheckbox рендерит опцию с двумя строками,
 * подзаголовком и чекбоксом.
 *
 * @public
 * @param option - Объект опции.
 * @param value - Массив выбранных значений.
 * @returns  Компонент опции с чекбоксом.
 */
export const renderTwoLineOptionWithSubtitleAndCheckbox = (
  option: Option,
  value: string[] | undefined,
) => {
  const [titleFirst, subtitle, ...rest] = option.label.split(', ');

  const isDisabled = option.disabled;
  const tooltipText = option.tooltipText;

  const title = titleFirst;

  const formattedSubtitle = `${subtitle || ''}${
    !!rest.length ? `, ${rest.join(', ')}` : ''
  }`;

  const handleStopPropagination = (event: MouseEvent<HTMLElement>) =>
    event?.stopPropagation();

  if (isDisabled) {
    return (
      <Box
        py="12"
        display="flex"
        alignItems="center"
        onClick={handleStopPropagination}
      >
        <Checkbox
          withoutMargin
          sizeVariant="large"
          checked={
            !!value && value.some((item: string) => item === option.value)
          }
        />
        <Tooltip title={tooltipText as string} placement="right">
          <Box display="flex" flexDirection="column">
            {title && (
              <Box>
                <Typography variant="body3">{title}</Typography>
              </Box>
            )}
            <Box
              display="flex"
              alignItems="center"
              flexDirection="row"
              gap="8"
              mt="4"
            >
              {title !== subtitle && (
                <Box order={1}>
                  <Typography
                    component="div"
                    variant={title ? 'caption' : 'body3'}
                    color="text.light.tertirary"
                  >
                    {formattedSubtitle}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box py="12" display="flex" alignItems="center">
      <Box>
        <Checkbox
          withoutMargin
          sizeVariant="large"
          checked={
            !!value && value.some((item: string) => item === option.value)
          }
        />
      </Box>
      <Box display="flex" flexDirection="column">
        {title && (
          <Box>
            <Typography variant="body3">{title}</Typography>
          </Box>
        )}
        <Box
          display="flex"
          alignItems="center"
          flexDirection="row"
          gap="8"
          mt="4"
        >
          {title !== subtitle && (
            <Box order={1}>
              <Typography
                component="div"
                variant={title ? 'caption' : 'body3'}
                color="text.light.tertirary"
              >
                {formattedSubtitle}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Функция renderTwoLineOptionWithCheckbox рендерит опцию с двумя строками и чекбоксом.
 *
 * @public
 * @param option - Объект опции.
 * @param value - Массив выбранных значений.
 * @returns  Компонент опции с чекбоксом.
 */
export const renderTwoLineOptionWithCheckbox = (
  option: Option,
  value: string[] | undefined,
) => {
  const [titleFirst, titleSecond, subtitle, ...rest] = option.label.split(', ');

  const { badge } = option;
  const isBadgePositionLeft = badge?.position === 'left';
  const isBadgeHasTitle = !!badge?.title;
  const isDisabled = option.disabled;
  const tooltipText = option.tooltipText;

  let title = '';

  if (subtitle && !titleFirst) {
    title = subtitle;
  } else {
    const titleSecondFormatted = titleSecond ? `, ${titleSecond}` : '';
    title = `${titleFirst || ''}${titleSecondFormatted}`;
  }

  const formattedSubtitle = `${subtitle}${
    !!rest.length ? `, ${rest.join(', ')}` : ''
  }`;

  const handleStopPropagination = (event: MouseEvent<HTMLElement>) =>
    event?.stopPropagation();

  if (isDisabled) {
    return (
      <Box
        py="12"
        display="flex"
        alignItems="center"
        width="100%"
        onClick={handleStopPropagination}
      >
        <Checkbox
          withoutMargin
          sizeVariant="large"
          checked={
            !!value && value.some((item: string) => item === option.value)
          }
        />
        <Tooltip title={tooltipText as string} placement="right">
          <Box display="flex" flexDirection="column" width="100%">
            {title && (
              <Box display="flex" alignItems="center" gap="8">
                {badge && isBadgeHasTitle && isBadgePositionLeft && (
                  <Badge
                    title={badge.title}
                    description={badge.description}
                    color={badge.color}
                  />
                )}
                <Typography variant="body3">{title}</Typography>
              </Box>
            )}
            {title !== subtitle && (
              <Box
                display="flex"
                alignItems="center"
                flexDirection="row"
                gap="8"
                mt="4"
              >
                <Box order={1}>
                  <Typography
                    component="div"
                    variant={title ? 'caption' : 'body3'}
                    color="text.light.tertirary"
                  >
                    {formattedSubtitle}
                  </Typography>
                </Box>
              </Box>
            )}
            {badge && isBadgeHasTitle && !isBadgePositionLeft && (
              <Box mt="4">
                <Badge
                  title={badge.title}
                  description={badge.description}
                  color={badge.color}
                />
              </Box>
            )}
          </Box>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box py="12" display="flex" alignItems="center" width="100%">
      <Box>
        <Checkbox
          withoutMargin
          sizeVariant="large"
          checked={
            !!value && value.some((item: string) => item === option.value)
          }
        />
      </Box>
      <Tooltip
        disabled={!tooltipText}
        title={tooltipText as string}
        placement="right"
      >
        <Box display="flex" flexDirection="column" width="100%">
          {title && (
            <Box display="flex" alignItems="center" gap="8">
              {badge && isBadgeHasTitle && isBadgePositionLeft && (
                <Badge
                  title={badge.title}
                  description={badge.description}
                  color={badge.color}
                />
              )}
              <Typography variant="body3">{title}</Typography>
            </Box>
          )}
          {subtitle && title !== subtitle && (
            <Box
              display="flex"
              alignItems="center"
              flexDirection="row"
              gap="8"
              mt="4"
            >
              <Box order={1}>
                <Typography
                  component="div"
                  variant={title ? 'caption' : 'body3'}
                  color="text.light.tertirary"
                >
                  <EllipsisText>{formattedSubtitle}</EllipsisText>
                </Typography>
              </Box>
            </Box>
          )}
          {badge && isBadgeHasTitle && !isBadgePositionLeft && (
            <Box mt="4">
              <Badge
                title={badge.title}
                description={badge.description}
                color={badge.color}
              />
            </Box>
          )}
        </Box>
      </Tooltip>
    </Box>
  );
};

/**
 * Функция renderOptionWithIcon рендерит опцию с иконкой.
 *
 * @public
 * @param option - Объект опции, содержащий icon.
 * @returns  Компонент опции с иконкой.
 *
 * @example
 * ```
 * <Box>
 *   {renderOptionWithIcon({ label: 'Заголовок', })}
 * </Box>
 * ```
 */
export const renderOptionWithIcon = (option: ExtendedOption) => {
  const { label, startIcon } = option;

  return (
    <Box display="flex" alignItems="center" gap="12" py="12">
      {startIcon && startIcon}
      <Box>
        <Typography variant="body3">{label}</Typography>
      </Box>
    </Box>
  );
};
